import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Copy, Download, FileText, Lock, Maximize2, Phone, Undo2, X } from "lucide-react";
import {
  COLUMNAS,
  esPreguntaProhibida,
  ETIQUETA_COLUMNA,
  fechaAmigable,
  linkLlamar,
  linkWhatsApp,
  telefonoLegible,
} from "@/reclutamiento/logica";
import { esClavePlaza, plazaDe } from "@/reclutamiento/plazas";
import type { Columna } from "@/reclutamiento/tipos";
import type { FichaPortal } from "../api";
import { CajaError, Esqueleto, HojaDescartar } from "../componentes";
import { TITULO_VEREDICTO } from "../claridad-textos";
import { AVISO_SOLO_LECTURA, usePortal } from "../contexto";
import { IcWhatsApp, IconoEtapa, Mk, MkEstado } from "../iconos";
import { claseEtapa } from "../etapas";
import { descartarCandidato, moverCandidato, regresarCandidato } from "../mover";
import { guardarCambio, hayPendiente, usePendientes } from "../pendientes";
import { fichaPrecargada, olvidarFicha, precargarFicha } from "../precarga";
import { navegar, ruta } from "../ruta";
import {
  clavePestana,
  clavePlaza,
  esPestana,
  esVista,
  etiquetaVista,
  guardarSecuencia,
  leerEstadoLista,
  leerSecuencia,
  ordenDeVista,
  posicion,
  type Pestana,
  type Secuencia,
} from "../secuencia";
import { claveCache, copiar, ETIQUETA_ESTADO, leerPref, mensajeError, semDeEstado, semDeVeredicto, useAltoComoVariable, useAngosto, useCarga } from "../util";
import { porQue } from "./Candidatos";

/** Guardar la nota escrita y no guardada antes de irse (Anterior, Siguiente o Candidatos). true = se puede ir. */
type Pendiente = MutableRefObject<(() => Promise<boolean>) | null>;

/**
 * La secuencia de la vista de donde vino (para «3 de 28»). Si se abrió por liga directa o la secuencia guardada
 * no lo trae, se arma igual que la vista recordada (vista, pestaña y orden de la Lista) con los de su vacante.
 */
function useSecuencia(id: string, vacanteId: string | null): Secuencia | null {
  const { api, sesion } = usePortal();
  const [sec, setSec] = useState<Secuencia | null>(() => {
    const s = leerSecuencia();
    return s?.ids.includes(id) ? s : null;
  });
  useEffect(() => {
    if (sec?.ids.includes(id) || !vacanteId) return;
    let vivo = true;
    api
      .candidatos(vacanteId)
      .then((items) => {
        const pv = leerPref("vista");
        const vista = esVista(pv) ? pv : "tablero";
        const pp = leerPref(clavePestana(sesion));
        let pestana: Pestana = esPestana(pp) ? pp : "todos";
        const pz = leerPref(clavePlaza(sesion));
        const enPlaza = (k: { ciudad: string | null }) => !esClavePlaza(pz) || plazaDe(k.ciudad) === pz;
        let ids = ordenDeVista(items, vista, pestana, leerEstadoLista(), "", enPlaza).map((k) => k.id);
        if (!ids.includes(id)) {
          pestana = "todos";
          ids = ordenDeVista(items, vista, pestana, leerEstadoLista(), "", enPlaza).map((k) => k.id);
        }
        if (!ids.includes(id)) ids = [id, ...ids];
        const nueva = {
          ids,
          volver: ruta("candidatos", { vacante: vacanteId, vista, grupo: pestana }),
          etiqueta: etiquetaVista(pestana, vista),
        };
        guardarSecuencia(nueva);
        if (vivo) setSec(nueva);
      })
      .catch(() => {
        /* sin secuencia: la ficha funciona igual, solo sin Anterior/Siguiente */
      });
    return () => {
      vivo = false;
    };
  }, [api, sesion, id, vacanteId, sec]);
  return sec;
}

export function Ficha({ id }: { id: string }) {
  const { api, sesion } = usePortal();
  // Si la ficha venía precargada (Anterior/Siguiente), se pinta al instante; la versión fresca SIEMPRE se pide
  // a la API y la reemplaza (así nunca se queda en pantalla una nota, etapa o motivo viejo).
  const carga = useCarga(
    () => api.candidato(id),
    [api, id],
    () => fichaPrecargada(id),
    claveCache(sesion, api.verComo, "ficha:" + id),
  );
  useEffect(() => {
    if (carga.datos && !carga.cargando) olvidarFicha(id);
  }, [id, carga.datos, carga.cargando]);

  const sec = useSecuencia(id, carga.datos?.vacante_id ?? null);
  const pos = useMemo(() => (sec ? posicion(sec.ids, id) : null), [sec, id]);
  const volver = sec?.volver ?? ruta("candidatos", { vacante: carga.datos?.vacante_id ?? null });
  const pendiente: Pendiente = useRef(null);

  // Precarga los de al lado (ficha y CV) para que el cambio sea inmediato.
  useEffect(() => {
    if (!pos) return;
    const t = setTimeout(() => {
      if (pos.siguiente) precargarFicha(api, pos.siguiente);
      if (pos.anterior) precargarFicha(api, pos.anterior);
    }, 250);
    return () => clearTimeout(t);
  }, [api, pos]);

  const yendo = useRef(false);
  const ir = useCallback(async (destino: string | null) => {
    if (!destino || yendo.current) return;
    yendo.current = true;
    try {
      if (pendiente.current && !(await pendiente.current())) return; // la nota no se pudo guardar: se queda
      if (destino.startsWith("#")) navegar(destino);
      // replace: el «atrás» del navegador regresa a la lista, no ficha por ficha
      else navegar(`#/candidato/${destino}`, true);
    } finally {
      yendo.current = false;
    }
  }, []);

  // Flechas ← → del teclado (no mientras escribes, ni con una hoja o el CV en grande abiertos).
  useEffect(() => {
    if (!pos) return;
    const tecla = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const t = e.target as HTMLElement | null;
      if (t?.closest?.("input, textarea, select, [contenteditable='true'], [contenteditable='']")) return;
      if (document.querySelector(".hoja-panel, .cvv")) return;
      e.preventDefault();
      void ir(e.key === "ArrowLeft" ? pos.anterior : pos.siguiente);
    };
    document.addEventListener("keydown", tecla);
    return () => document.removeEventListener("keydown", tecla);
  }, [pos, ir]);

  // Deslizar en celular: hacia la izquierda = siguiente, hacia la derecha = anterior.
  const toque = useRef<{ x: number; y: number; t: number } | null>(null);
  const alTocar = (e: React.TouchEvent) => {
    const el = e.target as HTMLElement;
    if (e.touches.length !== 1 || el.closest("textarea, input, select, .cvbox, .stagepick")) {
      toque.current = null;
      return;
    }
    toque.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
  };
  const alSoltar = (e: React.TouchEvent) => {
    const i = toque.current;
    toque.current = null;
    if (!i || !pos || document.querySelector(".hoja-panel, .cvv")) return;
    const dx = e.changedTouches[0].clientX - i.x;
    const dy = e.changedTouches[0].clientY - i.y;
    if (Math.abs(dx) < 70 || Math.abs(dy) > Math.abs(dx) * 0.6 || Date.now() - i.t > 800) return;
    void ir(dx < 0 ? pos.siguiente : pos.anterior);
  };

  return (
    <>
      <NavFicha pos={pos} etiqueta={sec?.etiqueta ?? null} volver={volver} ir={ir} />
      <div className="fwrap" onTouchStart={alTocar} onTouchEnd={alSoltar}>
        {carga.cargando && !carga.datos ? (
          <Esqueleto filas={3} alto={140} />
        ) : carga.error ? (
          <CajaError texto={carga.error} onReintentar={carga.recargar} />
        ) : carga.datos?.bloqueado ? (
          <Bloqueada />
        ) : carga.datos ? (
          <Contenido f={carga.datos} setF={(fn) => carga.setDatos((x) => (x ? fn(x) : x))} pendiente={pendiente} />
        ) : null}
      </div>
    </>
  );
}

/** ‹ Anterior | 3 de 28 | Siguiente › — fija arriba, debajo del encabezado, para tenerla siempre a la mano. */
function NavFicha({
  pos,
  etiqueta,
  volver,
  ir,
}: {
  pos: ReturnType<typeof posicion>;
  etiqueta: string | null;
  volver: string;
  ir: (destino: string | null) => Promise<void>;
}) {
  const angosto = useAngosto(640);
  const barra = useRef<HTMLElement>(null);
  useAltoComoVariable(barra, "--fnav-h");
  return (
    <nav className="fnav" aria-label="Recorrer candidatos" ref={barra}>
      <div className="fnav-in">
        <a
          className="btn ghost fnav-volver"
          href={volver}
          onClick={(e) => {
            e.preventDefault();
            void ir(volver);
          }}
          aria-label={`Volver a ${etiqueta ?? "Candidatos"}`}
        >
          <ArrowLeft size={18} aria-hidden="true" />
          <span className="fnav-et">{etiqueta ?? "Candidatos"}</span>
          <span className="fnav-corto">Volver</span>
        </a>
        <div className="fnav-pasos">
          <button
            className="btn fnav-b"
            disabled={!pos?.anterior}
            onClick={() => void ir(pos?.anterior ?? null)}
            aria-label="Candidato anterior"
            title="Anterior (tecla ←)"
          >
            <ChevronLeft size={20} aria-hidden="true" />
            <span className="fnav-t">Anterior</span>
          </button>
          <span className="fnav-pos num" aria-live="polite">
            {pos ? (
              <>
                <b>{pos.n}</b> de {pos.total}
              </>
            ) : (
              "…"
            )}
          </span>
          <button
            className="btn primary fnav-b"
            disabled={!pos?.siguiente}
            onClick={() => void ir(pos?.siguiente ?? null)}
            aria-label="Siguiente candidato"
            title="Siguiente (tecla →)"
          >
            <span className="fnav-t">Siguiente</span>
            <ChevronRight size={20} aria-hidden="true" />
          </button>
        </div>
      </div>
      {pos && pos.total > 1 && (
        <p className="fnav-tip">
          {angosto ? "Desliza a los lados para pasar de candidato" : "También con las flechas ← → del teclado"}
        </p>
      )}
    </nav>
  );
}

function Bloqueada() {
  return (
    <div className="vcard locked-panel" style={{ padding: 20 }}>
      <span className="lk">
        <Lock size={28} aria-hidden="true" />
      </span>
      <h1 style={{ fontSize: 24 }}>Esta ficha está bloqueada</h1>
      <p className="muted">
        Las fichas se liberan al confirmar tu pago. Mientras, ves cuántos candidatos hay y en qué etapa van.
      </p>
      <a className="btn primary" href="#/cuenta">
        Ver cómo pagar
      </a>
    </div>
  );
}

function Contenido({
  f,
  setF,
  pendiente,
}: {
  f: FichaPortal;
  setF: (fn: (x: FichaPortal) => FichaPortal) => void;
  pendiente: Pendiente;
}) {
  const { api, soloLectura, sesion } = usePortal();
  const sem = semDeVeredicto(f.veredicto);
  const wa = linkWhatsApp(f.telefono, f.nombre, f.vacante?.puesto, f.empresa, sesion.cliente.mensaje_whatsapp);
  const tel = linkLlamar(f.telefono);
  const telTxt = telefonoLegible(f.telefono);

  const [pidiendoMotivo, setPidiendoMotivo] = useState(false);
  const aplicar = (_id: string, fn: (x: FichaPortal) => FichaPortal) => setF(fn);
  const mover = (col: Columna) => moverCandidato(api, f, col, aplicar, soloLectura, () => setPidiendoMotivo(true));

  const respuestas = Object.entries(f.respuestas ?? {}).filter(([q]) => !esPreguntaProhibida(q));
  const criterios = (f.vacante?.criterios ?? []).map((cv) => ({ cv, ck: f.criterios.find((x) => x.clave === cv.clave) }));
  // criterios del candidato que no están en la vacante (por si la vacante cambió)
  const sueltos = f.criterios.filter((ck) => !f.vacante?.criterios.some((cv) => cv.clave === ck.clave));

  return (
    <div className="fcols">
      <div className="f-a">
        <div className="fh">
          <p className="eyebrow">
            {f.vacante?.puesto ?? "Vacante"}
            {f.vacante?.zona ? ` · ${f.vacante.zona}` : ""}
          </p>
          <h1>{f.nombre}</h1>
          <p>
            {f.ciudad ? `${f.ciudad} · ` : ""}llegó {fechaAmigable(f.recibido_en, f.ingresado_en)}
          </p>
        </div>
        <div className={`verdict ${sem}`}>
          <Mk k={sem} s={42} />
          <div>
            <div className="vl">{TITULO_VEREDICTO[f.veredicto]}</div>
            <p>{porQue(f)}</p>
          </div>
        </div>
        <div className="acts2">
          {wa ? (
            <a className="btn wa big" href={wa} target="_blank" rel="noopener noreferrer">
              <IcWhatsApp s={23} relleno /> WhatsApp
            </a>
          ) : (
            <button className="btn big" disabled>
              Sin WhatsApp
            </button>
          )}
          {tel ? (
            <a className="btn big" href={tel}>
              <Phone size={19} aria-hidden="true" /> Llamar
            </a>
          ) : (
            <button className="btn big" disabled>
              Sin teléfono
            </button>
          )}
        </div>
        {f.telefono && (
          <div className="telrow">
            <span>Teléfono:</span>
            <span className="num">{telTxt}</span>
            <button
              className="mini"
              onClick={async () => (await copiar(telTxt)) ? toast.success("Teléfono copiado") : toast.error("No se pudo copiar")}
            >
              <Copy size={13} aria-hidden="true" /> Copiar
            </button>
            <span style={{ fontSize: 12.5 }}>El saludo de WhatsApp ya dice la vacante.</span>
          </div>
        )}

        <section className="fsec" aria-labelledby="h-etapa">
          <h2 id="h-etapa">¿En qué etapa va?</h2>
          <p className="fsec-sub">Toca la etapa nueva cuando le escribas, lo entrevistes o lo contrates.</p>
          <div className="stagepick" role="group" aria-label="Mover de etapa">
            {COLUMNAS.map((c) => {
              const esDescarte = c === "descartado";
              return (
                <button key={c} className={claseEtapa(c)} aria-pressed={f.columna === c} disabled={soloLectura} onClick={() => mover(c)}>
                  <IconoEtapa c={c} s={26} />
                  {esDescarte ? (f.etapa === "descartado" ? "Descartado" : "Descartar…") : ETIQUETA_COLUMNA[c]}
                </button>
              );
            })}
          </div>
          {f.etapa === "descartado" ? (
            <div className="descarte" role="status">
              <span>
                <b>Lo descartaste</b>
                {f.motivo_cliente ? `: ${f.motivo_cliente}` : ""}
              </span>
              <span className="spacer" />
              <button className="mini" disabled={soloLectura} onClick={() => setPidiendoMotivo(true)}>
                Cambiar motivo
              </button>
              <button className="mini" disabled={soloLectura} onClick={() => regresarCandidato(api, f, aplicar, soloLectura)}>
                <Undo2 size={13} aria-hidden="true" /> Regresar a activos
              </button>
            </div>
          ) : f.etapa === "algun_dia" ? (
            <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
              Lo guardaste para algún día: no cuenta como activo. Cuando quieras, muévelo a cualquier etapa.
            </p>
          ) : f.columna === "descartado" ? (
            <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
              Lo descartó el filtro de la vacante. Si aun así te interesa, muévelo a Contactado.
            </p>
          ) : null}
          {pidiendoMotivo && (
            <HojaDescartar
              nombre={f.nombre ?? "este candidato"}
              inicial={f.etapa === "descartado" ? f.motivo_cliente : null}
              onConfirmar={(m) => descartarCandidato(api, f, m, aplicar, soloLectura)}
              onCerrar={() => setPidiendoMotivo(false)}
            />
          )}
        </section>

        {/* La nota va pegada a la etapa: se marca y se anota en el mismo lugar, antes de pasar al siguiente. */}
        <Nota key={f.id} f={f} setF={setF} pendiente={pendiente} />
      </div>

      <div className="f-cv">
        <VisorCv f={f} />
      </div>

      <div className="f-b">
        <section className="fsec" aria-labelledby="h-crit">
          <h2 id="h-crit">Requisitos, uno por uno</h2>
          <ul className="critl">
            {criterios.map(({ cv, ck }) => {
              const s = ck ? semDeEstado(ck.estado) : "yel";
              const est = ck ? ETIQUETA_ESTADO[ck.estado] : "Por confirmar";
              return (
                <li key={cv.clave}>
                  {ck ? <MkEstado e={ck.estado} s={26} titulo={est} /> : <MkEstado e="falta" s={26} titulo={est} />}
                  <div className="ct">
                    <b>{cv.texto}</b>
                    <span>
                      <span className={`est ${s}`}>{est}</span>
                      {" · "}
                      {ck?.dato || "Sin dato"}
                    </span>
                  </div>
                  <span className="rule">
                    {cv.efecto === "descarta" ? "Obligatorio" : cv.efecto === "marca" ? "Deseable" : "Informativo"}
                  </span>
                </li>
              );
            })}
            {sueltos.map((ck) => (
              <li key={ck.clave}>
                <MkEstado e={ck.estado} s={26} titulo={ETIQUETA_ESTADO[ck.estado]} />
                <div className="ct">
                  <b>{ck.dato || ck.clave}</b>
                  <span className={`est ${semDeEstado(ck.estado)}`}>{ETIQUETA_ESTADO[ck.estado]}</span>
                </div>
              </li>
            ))}
            {!criterios.length && !sueltos.length && <li className="muted">Esta vacante no tiene criterios cargados.</li>}
          </ul>
        </section>

        <section className="fsec" aria-labelledby="h-qa">
          <h2 id="h-qa">Respuestas del cuestionario</h2>
          {respuestas.length ? (
            <dl className="qa">
              {respuestas.map(([q, a]) => (
                <div key={q}>
                  <dt>{q}</dt>
                  <dd>{a}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="muted">Sin respuestas todavía.</p>
          )}
        </section>
      </div>
    </div>
  );
}

function VisorCv({ f }: { f: FichaPortal }) {
  const { api } = usePortal();
  const [url, setUrl] = useState<string | null | undefined>(undefined);
  const [grande, setGrande] = useState(false);

  useEffect(() => {
    let vivo = true;
    let creada: string | null = null;
    setUrl(undefined);
    api
      .urlCv(f.cv_url)
      .then((u) => {
        if (!vivo) {
          if (u?.startsWith("blob:")) URL.revokeObjectURL(u);
          return;
        }
        creada = u;
        setUrl(u);
      })
      .catch(() => vivo && setUrl(null));
    return () => {
      vivo = false;
      if (creada?.startsWith("blob:")) URL.revokeObjectURL(creada);
    };
  }, [api, f.cv_url]);

  useEffect(() => {
    if (!grande) return;
    const t = (e: KeyboardEvent) => e.key === "Escape" && setGrande(false);
    document.addEventListener("keydown", t);
    const ov = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", t);
      document.body.style.overflow = ov;
    };
  }, [grande]);

  const archivo = `CV ${f.nombre ?? "candidato"}.pdf`;
  const dijoQueSi = Object.entries(f.respuestas ?? {}).some(([q, a]) => /cv/i.test(q) && /^s[ií]/i.test(a.trim()));

  return (
    <section className="fsec" aria-labelledby="h-cv">
      <h2 id="h-cv">CV</h2>
      {url === undefined ? (
        <div className="sk" style={{ height: 380 }} />
      ) : url ? (
        <>
          <div className="cvbox">
            <iframe src={url} title={`CV de ${f.nombre}`} />
          </div>
          <div className="row" style={{ marginTop: 10 }}>
            <button className="btn" onClick={() => setGrande(true)}>
              <Maximize2 size={16} aria-hidden="true" /> Ver en grande
            </button>
            <a className="btn ghost" href={url} download={archivo}>
              <Download size={16} aria-hidden="true" /> Descargar
            </a>
          </div>
          {grande && (
            <div className="cvv" role="dialog" aria-modal="true" aria-label={`CV de ${f.nombre}`}>
              <div className="cvv-bar">
                <b>CV · {f.nombre}</b>
                <a className="btn" href={url} download={archivo} aria-label="Descargar CV">
                  <Download size={16} aria-hidden="true" />
                  <span className="cvv-t">Descargar</span>
                </a>
                <button className="btn primary" onClick={() => setGrande(false)} autoFocus>
                  <X size={16} aria-hidden="true" /> Cerrar
                </button>
              </div>
              <iframe src={url} title={`CV de ${f.nombre}, en grande`} />
            </div>
          )}
        </>
      ) : (
        <div className="empty" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
          <FileText size={26} aria-hidden="true" />
          <b style={{ color: "var(--ink)" }}>{f.cv_url || dijoQueSi ? "CV en la hoja" : "Sin CV todavía"}</b>
          <span>
            {f.cv_url || dijoQueSi
              ? "El CV está en la hoja de la agencia; pídeselo si lo necesitas."
              : "El candidato no ha mandado su CV."}
          </span>
        </div>
      )}
    </section>
  );
}

/**
 * Tu nota. Se muestra la que está guardada en la base (la ficha siempre la pide fresca), se guarda con el botón y,
 * si escribiste algo y te vas (Anterior, Siguiente o Candidatos) sin guardarlo, se guarda sola antes de irte.
 */
function Nota({
  f,
  setF,
  pendiente,
}: {
  f: FichaPortal;
  setF: (fn: (x: FichaPortal) => FichaPortal) => void;
  pendiente: Pendiente;
}) {
  const { api, soloLectura } = usePortal();
  const guardadaEnBase = f.nota_cliente ?? "";
  const [texto, setTexto] = useState(guardadaEnBase);
  const [guardando, setGuardando] = useState(false);
  // Si llega una versión más fresca de la ficha con otra nota y no la estás editando, se muestra esa.
  const anterior = useRef(guardadaEnBase);
  useEffect(() => {
    setTexto((t) => (t.trim() === anterior.current.trim() ? guardadaEnBase : t));
    anterior.current = guardadaEnBase;
  }, [guardadaEnBase]);

  const sinGuardar = texto.trim() !== guardadaEnBase.trim();
  const cola = usePendientes();
  const enCola = hayPendiente(cola, "nota", f.id);
  const guardar = useCallback(async (): Promise<boolean> => {
    if (soloLectura) {
      toast.error(AVISO_SOLO_LECTURA);
      return false;
    }
    setGuardando(true);
    try {
      const r = await guardarCambio(api, { tipo: "nota", id: f.id, nombre: f.nombre, nota: texto });
      // Guardada o en cola, lo que escribiste se queda: si no hubo red, se manda sola al reconectar.
      setF((x) => ({ ...x, nota_cliente: texto.trim() || null }));
      if (r === "en-cola") toast.info("Sin conexión: tu nota se guardará al reconectar.");
      else toast.success(texto.trim() ? "Nota guardada" : "Nota borrada");
      return true;
    } catch (e) {
      toast.error(mensajeError(e, "No se pudo guardar la nota."));
      return false;
    } finally {
      setGuardando(false);
    }
  }, [api, f.id, f.nombre, texto, setF, soloLectura]);

  useEffect(() => {
    pendiente.current = sinGuardar && !soloLectura ? guardar : null;
    return () => {
      pendiente.current = null;
    };
  }, [pendiente, sinGuardar, soloLectura, guardar]);

  return (
    <section className="fsec" aria-labelledby="h-nota">
      <h2 id="h-nota">Tu nota</h2>
      <label className="sr" htmlFor={`nota-${f.id}`}>
        Tu nota sobre {f.nombre}
      </label>
      <textarea
        id={`nota-${f.id}`}
        className="inp"
        value={texto}
        disabled={soloLectura}
        onChange={(e) => setTexto(e.target.value)}
        placeholder="Ej.: le marqué el martes, quedó de venir el jueves a las 10."
      />
      <div className="row" style={{ marginTop: 8 }}>
        <button className="btn" onClick={() => void guardar()} disabled={soloLectura || guardando || !sinGuardar}>
          {guardando ? "Guardando…" : "Guardar nota"}
        </button>
        {enCola && !sinGuardar ? (
          <span className="muted nota-cola" role="status" style={{ fontSize: 13 }}>
            {cola.guardando ? "Guardando…" : "Sin conexión, se guardará al reconectar"}
          </span>
        ) : (
          guardadaEnBase &&
          !sinGuardar && (
            <span className="muted nota-ok" role="status" style={{ fontSize: 13 }}>
              Guardada
            </span>
          )
        )}
        {sinGuardar && !soloLectura && (
          <span className="muted" style={{ fontSize: 13 }}>
            Sin guardar · se guarda sola si pasas a otro candidato
          </span>
        )}
      </div>
    </section>
  );
}
